import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, inject } from '@angular/core';
import {
    FormControl,
    FormsModule,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import {
    createMask,
    InputMaskModule,
    InputmaskOptions,
} from '@ngneat/input-mask';
import { TranslocoPipe } from '@jsverse/transloco';
import {
    UntilDestroy,
    untilDestroyed,
} from '@ngneat/until-destroy';
import { EMPTY } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { ValidPhonesService } from '~/app/core/services/api/valid-phones/valid-phones.service';
import { AuthenticationFacade } from '~/app/core/state/authentication/authentication.facade';
import { AlertComponent } from '~/app/shared/components/elements/alert/alert.component';
import { ButtonComponent } from '~/app/shared/components/elements/buttons/button/button.component';
import { H2Component } from '~/app/shared/components/elements/h2/h2.component';
import { IconComponent } from '~/app/shared/components/elements/icon/icon.component';
import { LabelComponent } from '~/app/shared/components/elements/label/label.component';
import { MaterialInputPhoneNumberComponent } from '~/app/shared/components/forms/formElements/material-input-phone-number/material-input-phone-number.component';
import { ModalRef } from '~/app/shared/services/modal/modal-ref';
import {
    ButtonWithIconComponent
} from "~/app/shared/components/elements/buttons/button-with-icon/button-with-icon.component";

@UntilDestroy()
@Component({
    selector: 'eb-valid-phones-number',
    templateUrl: './valid-phone-number.component.html',
    styleUrls: ['./valid-phone-number.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        H2Component,
        LabelComponent,
        IconComponent,
        AlertComponent,
        MaterialInputPhoneNumberComponent,
        FormsModule,
        ReactiveFormsModule,
        InputMaskModule,
        ButtonComponent,
        TranslocoPipe,
        ButtonWithIconComponent,
    ]
})
export class ValidPhoneNumberComponent implements OnDestroy {
    private validPhonesService = inject(ValidPhonesService);
    private authenticationFacade = inject(AuthenticationFacade);
    private cd = inject(ChangeDetectorRef);
    private ref = inject<ModalRef<boolean | Record<string, unknown>, {
    sessionCount: number;
}>>(ModalRef);

    public smsSent = false;

    public loading = false;

    public maskInput: InputmaskOptions<unknown> | null = null;

    public sessionCount = this.ref.data.sessionCount;

    public error = false;

    public errorMsg = '';

    public fcPhoneNumber = new FormControl<string>(this.authenticationFacade.getUserSnapshot()?.phone ?? '', {
        nonNullable: true,
        validators: [Validators.required],
    });

    public fcCode = new FormControl<string>('', {
        nonNullable: true,
        validators: [Validators.required],
    });

    public requestId = '';

    public success = false;

    private timeoutRef: any;

    constructor() {
        this.maskInput = createMask({
            mask: '9     9     9     9     9     9',
            placeholder: '',
            unmaskAsNumber: true,
            autoUnmask: true,
            showMaskOnFocus: true,
            showMaskOnHover: true,
            digits: 6,
        });
    }

    public close() {
        this.ref.close({});
    }

    public sendCode() {
        this.loading = true;
        this.error = false;
        this.validPhonesService.sendPhoneCode(this.fcPhoneNumber.value).pipe(
            catchError((error: HttpErrorResponse) => {
                this.loading = false;

                this.errorMsg = error.error?.message ?? error.error?.detail;
                this.error = true;
                this.cd.detectChanges();
                return EMPTY;
            }),
            untilDestroyed(this),
        ).subscribe((result) => {
            this.loading = false;
            this.smsSent = true;
            this.requestId = result.requestId;
            this.cd.detectChanges();
        });
    }

    public validCode() {
        this.loading = true;
        this.error = false;
        this.validPhonesService.validPhoneCode(this.fcPhoneNumber.value, this.requestId, this.fcCode.value).pipe(
            catchError((error: HttpErrorResponse) => {
                this.loading = false;

                this.errorMsg = error.error?.message ?? error.error?.detail;
                this.error = true;
                this.cd.detectChanges();
                return EMPTY;
            }),
            untilDestroyed(this),
        ).subscribe(() => {
            this.loading = false;
            this.success = true;
            const user = this.authenticationFacade.getUserSnapshot();
            if (user) {
                this.authenticationFacade.updateUser({
                    ...user,
                    phoneVerified: true,
                });
            }
            this.cd.detectChanges();
            this.timeoutRef = setTimeout(() => {
                this.ref.close(true);
            }, 3000);
        });
    }

    public back() {
        this.smsSent = false;
    }

    ngOnDestroy(): void {
        if (this.timeoutRef) {
            clearTimeout(this.timeoutRef);
        }
    }
}
